import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchCategory, fetchBrands, fetchProducts, fetchBrandSeries, catalogDownloadUrl, catalogViewUrl, seriesCatalogUrl, seriesCatalogViewUrl, productImageSrc } from "../lib/api";
import { Download, Eye, ArrowLeft, Package, ChevronRight, Filter, ChevronDown, ChevronUp } from "lucide-react";

export default function CategoryPage() {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeSubtype, setActiveSubtype] = useState("all");
  const [activeBrandId, setActiveBrandId] = useState("all");
  const [brandSeries, setBrandSeries] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setActiveSubtype("all");
    setActiveBrandId("all");
    setBrandSeries([]);
    Promise.all([
      fetchCategory(categoryId),
      fetchBrands(categoryId),
      fetchProducts({ category_id: categoryId }),
    ])
      .then(([cat, b, p]) => {
        setCategory(cat);
        setBrands(b);
        setProducts(p);
      })
      .catch(() => {});
  }, [categoryId]);

  // Load per-brand series when a specific brand is selected
  useEffect(() => {
    if (activeBrandId === "all") {
      setBrandSeries([]);
      return;
    }
    fetchBrandSeries(activeBrandId)
      .then(setBrandSeries)
      .catch(() => setBrandSeries([]));
  }, [activeBrandId]);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (activeBrandId !== "all" && p.brand_id !== activeBrandId) return false;
      if (activeSubtype === "all") return true;
      if (activeSubtype.startsWith("series:")) {
        const sid = activeSubtype.slice(7);
        return p.series_id === sid;
      }
      return p.subtype === activeSubtype;
    });
  }, [products, activeSubtype, activeBrandId]);

  if (!category) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center text-slate-400">
        Loading…
      </div>
    );
  }

  return (
    <div className="bg-slate-50">
      {/* Hero */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6"
            data-testid="back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600 mb-4">
                Category
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                {category.name}.
              </h1>
              <p className="mt-5 text-lg text-slate-600 max-w-xl">
                {category.description}
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[5/3] rounded-2xl overflow-hidden bg-slate-100">
                <img
                  src={productImageSrc(category.image)}
                  alt={category.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-12 z-30 bg-slate-50/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-700 hover:border-amber-400 md:hidden"
            data-testid="filters-toggle"
          >
            <span className="inline-flex items-center gap-2">
              <Filter className="w-4 h-4" />
              {activeBrandId === "all"
                ? "Filter by brand"
                : `Brand: ${brands.find((b) => b.id === activeBrandId)?.name || ""}`}
              {activeSubtype.startsWith("series:") && (
                <span className="text-amber-600">
                  · {brandSeries.find((s) => `series:${s.id}` === activeSubtype)?.name}
                </span>
              )}
            </span>
            {filtersOpen ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
          <div
            className={`${filtersOpen ? "block" : "hidden"} md:block mt-3 md:mt-0 space-y-3`}
          >
          {activeBrandId !== "all" && brandSeries.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-[0.2em] text-slate-400 font-bold mr-2">
                Series:
              </span>
              <button
                onClick={() => setActiveSubtype("all")}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeSubtype === "all"
                    ? "bg-slate-900 text-white"
                    : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}
                data-testid="series-all"
              >
                All ranges
              </button>
              {brandSeries.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveSubtype(`series:${s.id}`)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    activeSubtype === `series:${s.id}`
                      ? "bg-slate-900 text-white"
                      : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                  }`}
                  data-testid={`series-${s.id}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          ) : category.subtypes.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-[0.2em] text-slate-400 font-bold mr-2">
                Type:
              </span>
              <button
                onClick={() => setActiveSubtype("all")}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeSubtype === "all"
                    ? "bg-slate-900 text-white"
                    : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}
                data-testid="subtype-all"
              >
                All
              </button>
              {category.subtypes.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveSubtype(s.id)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    activeSubtype === s.id
                      ? "bg-slate-900 text-white"
                      : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                  }`}
                  data-testid={`subtype-${s.id}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-400 font-bold mr-2">
              Brand:
            </span>
            <button
              onClick={() => setActiveBrandId("all")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeBrandId === "all"
                  ? "bg-amber-500 text-slate-900"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
              }`}
              data-testid="brand-all"
            >
              All Brands
            </button>
            {brands.map((b) => (
              <button
                key={b.id}
                onClick={() => setActiveBrandId(b.id)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeBrandId === b.id
                    ? "bg-amber-500 text-slate-900"
                    : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}
                data-testid={`brand-${b.id}`}
              >
                {b.name}
              </button>
            ))}
          </div>
          </div>
        </div>
      </section>

      {/* Brand cards (when "All Brands") */}
      {activeBrandId === "all" && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-8">
            Brands we stock
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {brands.map((b) => (
              <button
                key={b.id}
                onClick={() => setActiveBrandId(b.id)}
                className="group text-left bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-amber-400 hover:-translate-y-0.5 hover:shadow-md transition-all flex flex-col"
                data-testid={`brand-card-${b.id}`}
              >
                <div className="aspect-[2/1] bg-white grid place-items-center p-3 sm:p-4 border-b border-slate-100 overflow-hidden">
                  {b.logo_url ? (
                    <img
                      src={productImageSrc(b.logo_url)}
                      alt={b.name}
                      className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <span className="font-heading text-xl font-semibold text-slate-700 text-center leading-tight">
                      {b.name}
                    </span>
                  )}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <p className="font-heading text-base font-semibold text-slate-900 leading-tight">
                    {b.name}
                  </p>
                  {b.tagline && (
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{b.tagline}</p>
                  )}
                  <div className="mt-auto pt-4 flex items-center justify-between text-xs">
                    {b.catalog_path ? (
                      <div className="inline-flex items-center gap-2">
                        <a
                          href={catalogViewUrl(b.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-amber-600 font-medium hover:text-amber-700"
                          data-testid={`brand-card-catalog-view-${b.id}`}
                          title="Open PDF"
                        >
                          <Eye className="w-3 h-3" /> View
                        </a>
                        <a
                          href={catalogDownloadUrl(b.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-slate-500 hover:text-amber-700"
                          data-testid={`brand-card-catalog-${b.id}`}
                          title="Download PDF"
                        >
                          <Download className="w-3 h-3" />
                        </a>
                      </div>
                    ) : (
                      <span className="text-slate-400">Catalog soon</span>
                    )}
                    <span className="text-slate-400 group-hover:text-amber-600 inline-flex items-center gap-0.5">
                      Open <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Selected brand banner */}
      {activeBrandId !== "all" && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
          {(() => {
            const brand = brands.find((b) => b.id === activeBrandId);
            if (!brand) return null;
            return (
              <div className="rounded-2xl bg-white border border-slate-200 p-6 lg:p-8 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-5">
                  {brand.logo_url && (
                    <div className="aspect-[2/1] w-32 sm:w-40 rounded-2xl bg-slate-50 border border-slate-100 grid place-items-center p-3 flex-shrink-0">
                      <img
                        src={productImageSrc(brand.logo_url)}
                        alt={brand.name}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  )}
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-amber-600 font-bold mb-1">
                      Brand
                    </p>
                    <h2 className="font-heading text-3xl font-semibold text-slate-900">
                      {brand.name}
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">{brand.tagline}</p>
                  </div>
                </div>
                {brand.catalog_path ? (
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={catalogViewUrl(brand.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-full font-medium text-sm hover:bg-amber-500 hover:text-slate-900 transition-colors"
                      data-testid={`brand-banner-view-${brand.id}`}
                    >
                      <Eye className="w-4 h-4" /> View catalog
                    </a>
                    <a
                      href={catalogDownloadUrl(brand.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-slate-900 border border-slate-200 rounded-full font-medium text-sm hover:bg-slate-100 transition-colors"
                      data-testid={`brand-banner-catalog-${brand.id}`}
                    >
                      <Download className="w-4 h-4" /> Download
                    </a>
                  </div>
                ) : (
                  <span className="text-sm text-slate-400 italic">
                    Brand catalog coming soon
                  </span>
                )}
              </div>
            );
          })()}
        </section>
      )}

      {/* Products — show all series as compact cards (3 per row) when "All ranges" is active */}
      {activeBrandId !== "all" && activeSubtype === "all" && brandSeries.length > 0 ? (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-6">
            Available series
            <span className="text-slate-400 font-normal text-base ml-2">
              ({brandSeries.length})
            </span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {brandSeries.map((s) => {
              const seriesProductImages = products
                .filter((p) => p.brand_id === activeBrandId && p.series_id === s.id)
                .flatMap((p) => p.image_urls || (p.image_url ? [p.image_url] : []))
                .filter(Boolean);
              return (
                <SeriesCard
                  key={s.id}
                  series={s}
                  images={seriesProductImages}
                  onView={() => setActiveSubtype(`series:${s.id}`)}
                />
              );
            })}
          </div>
        </section>
      ) : activeBrandId !== "all" && activeSubtype.startsWith("series:") ? (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-heading text-2xl font-semibold text-slate-900">
              Product samples
              <span className="text-slate-400 font-normal text-base ml-2">
                ({filteredProducts.length})
              </span>
            </h2>
            {activeBrandId !== "all" && activeSubtype.startsWith("series:") && (() => {
              const sid = activeSubtype.slice(7);
              const s = brandSeries.find((x) => x.id === sid);
              if (!s || !s.catalog_path) return null;
              return (
                <div className="flex flex-wrap gap-2">
                  <a
                    href={seriesCatalogViewUrl(s.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-full text-sm font-medium hover:bg-amber-500 hover:text-slate-900 transition-colors"
                    data-testid={`series-catalog-view-${s.id}`}
                  >
                    <Eye className="w-4 h-4" /> View {s.name}
                  </a>
                  <a
                    href={seriesCatalogUrl(s.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-900 border border-slate-200 rounded-full text-sm font-medium hover:bg-slate-100 transition-colors"
                    data-testid={`series-catalog-${s.id}`}
                  >
                    <Download className="w-4 h-4" /> Download
                  </a>
                </div>
              );
            })()}
          </div>
          {filteredProducts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center">
              <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No samples to show for this filter.</p>
              <Link
                to="/boq"
                className="inline-block mt-4 text-amber-600 font-medium hover:text-amber-700"
              >
                Send us a BOQ for a custom quote →
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {filteredProducts.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  subtypeLabel={
                    category.subtypes.find((s) => s.id === p.subtype)?.name ||
                    p.subtype
                  }
                />
              ))}
            </div>
          )}
        </section>
      ) : null}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="rounded-3xl bg-slate-900 text-white p-10 lg:p-12 flex flex-wrap items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl font-semibold">
              Need exact pricing for {category.name.toLowerCase()}?
            </h3>
            <p className="text-slate-400 mt-2 max-w-md">
              Share your requirement list — we'll respond with a brand-wise
              quotation within hours.
            </p>
          </div>
          <Link
            to="/boq"
            className="px-6 py-3 bg-amber-500 text-slate-900 rounded-full font-medium hover:bg-amber-400 transition-colors"
            data-testid="category-cta-boq"
          >
            Submit BOQ
          </Link>
        </div>
      </section>
    </div>
  );
}

function ProductCard({ product, subtypeLabel }) {
  const images = useMemo(() => {
    if (product.image_urls && product.image_urls.length > 0) return product.image_urls;
    if (product.image_url) return [product.image_url];
    return [];
  }, [product]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 2500);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <article
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all"
      data-testid={`product-${product.id}`}
    >
      <div className="relative aspect-square bg-slate-100 overflow-hidden">
        {images.length === 0 ? (
          <div className="w-full h-full grid place-items-center text-slate-300">
            <Package className="w-8 h-8" />
          </div>
        ) : (
          images.map((url, i) => (
            <img
              key={i}
              src={productImageSrc(url)}
              alt={product.name}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                i === idx ? "opacity-100" : "opacity-0"
              }`}
              onError={(e) => {
                e.currentTarget.style.opacity = "0.3";
              }}
            />
          ))
        )}
        {images.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-4 bg-white" : "w-1.5 bg-white/60"
                }`}
              />
            ))}
          </div>
        )}
      </div>
      <div className="p-4">
        {subtypeLabel && (
          <p className="text-[11px] uppercase tracking-[0.18em] text-amber-600 font-bold">
            {subtypeLabel}
          </p>
        )}
        <h3 className="font-heading text-base font-medium text-slate-900 mt-1 leading-snug line-clamp-2">
          {product.name}
        </h3>
      </div>
    </article>
  );
}

function SeriesCard({ series, images, onView }) {
  const [idx, setIdx] = useState(0);
  const hasImages = images && images.length > 0;

  useEffect(() => {
    if (!hasImages || images.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 2200);
    return () => clearInterval(t);
  }, [hasImages, images?.length]);

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-amber-400 hover:-translate-y-0.5 transition-all flex flex-col"
      data-testid={`series-card-${series.id}`}
    >
      {/* Photo area */}
      <div className="relative aspect-square bg-slate-50 overflow-hidden">
        {hasImages ? (
          images.map((url, i) => (
            <img
              key={i}
              src={productImageSrc(url)}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                i === idx ? "opacity-100" : "opacity-0"
              }`}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.opacity = "0";
              }}
            />
          ))
        ) : (
          <div className="w-full h-full grid place-items-center">
            <Package className="w-10 h-10 text-slate-300" />
          </div>
        )}

        {/* Slideshow dots */}
        {hasImages && images.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {images.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all ${
                  i === idx ? "w-4 bg-white" : "w-1 bg-white/60"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Caption — series name + actions */}
      <div className="p-4 flex flex-col flex-1">
        <p className="text-[10px] uppercase tracking-[0.2em] text-amber-600 font-bold">
          Series
        </p>
        <h3 className="font-heading text-base sm:text-lg font-semibold text-slate-900 mt-1 leading-snug line-clamp-2">
          {series.name}
        </h3>
        <div className="mt-auto pt-3 flex items-center justify-between gap-2">
          <button
            onClick={onView}
            className="inline-flex items-center gap-0.5 text-xs font-medium text-slate-500 hover:text-amber-600"
            data-testid={`series-view-${series.id}`}
          >
            View <ChevronRight className="w-3 h-3" />
          </button>
          {series.catalog_path ? (
            <div className="inline-flex items-center gap-1">
              <a
                href={seriesCatalogViewUrl(series.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-slate-900 text-white rounded-full text-[11px] font-medium hover:bg-amber-500 hover:text-slate-900 transition-colors"
                data-testid={`series-catalog-view-${series.id}`}
                title="Open PDF"
              >
                <Eye className="w-3 h-3" /> View
              </a>
              <a
                href={seriesCatalogUrl(series.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-7 h-7 bg-slate-100 text-slate-600 rounded-full hover:bg-amber-500 hover:text-slate-900 transition-colors"
                data-testid={`series-catalog-${series.id}`}
                title="Download PDF"
              >
                <Download className="w-3 h-3" />
              </a>
            </div>
          ) : (
            <span className="text-[11px] italic text-slate-400">Soon</span>
          )}
        </div>
      </div>
    </div>
  );
}

